import { DatePicker, DatePickerProps, Typography } from 'antd';
import dayjs from 'dayjs';
import { get } from 'lodash-es';
import { Control, Controller, FieldValues, Path } from 'react-hook-form';

import { getErrorStatus } from '../../utils/form';

import FieldWithLabel from './FieldWithLabel';
import { FieldProps } from './types';

type DateFieldProps<TFieldValues extends FieldValues> = DatePickerProps &
  FieldProps & {
    control: Control<TFieldValues>;
    name: Path<TFieldValues>;
    caption?: string;
  };

function DateField<TFieldValues extends FieldValues>({
  name,
  label,
  required,
  control,
  hideError,
  caption,
  ...props
}: DateFieldProps<TFieldValues>) {
  return (
    <Controller
      name={name}
      control={control}
      render={({ field: { onChange, value, ...rest }, formState }) => {
        const error = get(formState.errors, name);
        const errorMessage =
          !hideError && typeof error?.message === 'string'
            ? error.message
            : undefined;

        const status = !hideError && errorMessage ? getErrorStatus(error) : '';

        return (
          <FieldWithLabel label={label} required={required}>
            <DatePicker
              className={'w-full'}
              status={status}
              onChange={(v) => {
                onChange(v?.toISOString() || '');
              }}
              value={value ? dayjs(value) : null}
              {...rest}
              {...props}
            />
            {caption && (
              <Typography.Paragraph type='secondary' className='mb-0'>
                {caption}
              </Typography.Paragraph>
            )}
            {errorMessage && (
              <p className={'text-xs text-red-500'}>{errorMessage}</p>
            )}
          </FieldWithLabel>
        );
      }}
    />
  );
}

export default DateField;
